import React, { useEffect, useState } from "react";
import { ColumnsType } from "antd/es/table";
import { DashTransactionsResponse } from "../ceo-dashboard.service";
import CustomTable from "src/components/custom-table/custom-table";
import CustomSearch from "src/components/custom-search/custom-search";
import { useSelector } from "react-redux";
import moment from "moment";

interface TransactionsTableProps {
  data: DashTransactionsResponse | null;
  loading: boolean;
  onPageChange: (page: number, pageSize?: number) => void;
  onSearchChange: (searchWord: string) => void;
  onFilterChange: (filters: any) => void;
  currentFilters?: any;
}

const TransactionsTable: React.FC<TransactionsTableProps> = ({
  data,
  loading,
  onPageChange,
  onSearchChange,
  onFilterChange,
  currentFilters,
}) => {
  const user = useSelector((state: any) => state.user);
  let searchTimer = null;
  const [searchWord, setSearchWord] = useState(null);

  useEffect(() => {}, [searchWord]);

  const columns: ColumnsType<any> = [
    {
      title: "#",
      key: "index",
      width: "5%",
      render: (_, __, index) => {
        const currentPage = data?.data?.currentPage || 1;
        const pageSize = data?.data?.pageSize || 10;
        return (currentPage - 1) * pageSize + index + 1;
      },
    },
    {
      title: "Client name",
      dataIndex: "nameEn",
      key: "nameEn",
      sorter: (a, b) => a.nameEn.localeCompare(b.nameEn),
      render: (name: string) => (
        <div className="text-zinc-800 text-xs font-medium font-['Montserrat']">
          {user?.permissions?.includes("dashboard_trx_cname") ? name : "********"}
        </div>
      ),
    },
    {
      title: "Account number",
      dataIndex: "accountCode",
      key: "accountCode",
      render: (accountCode: number) => (
        <div className="text-zinc-800 text-xs font-medium font-['Montserrat']">
          {accountCode}
        </div>
      ),
    },
    {
      title: "Fund",
      dataIndex: "fundNameEn",
      key: "fundNameEn",
      render: (fundName: string) => (
        <div className="text-zinc-800 text-xs font-medium font-['Montserrat'] truncate max-w-xs">
          {fundName}
        </div>
      ),
    },
    {
      title: "Transaction Type",
      dataIndex: "transactiontype",
      key: "transactiontype",
      filters: [
        { text: "Subscription", value: "Subscription" },
        { text: "Redemption", value: "Redemption" },
        { text: "Deposit", value: "Deposit" },
        { text: "Withdrawal", value: "Withdrawal" },
      ],
      filteredValue: currentFilters?.filters?.transactiontype || null,
      render: (type: string) => (
        <div className="text-zinc-800 text-xs font-medium font-['Montserrat']">
          {type}
        </div>
      ),
    },
    {
      title: "Transaction date",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (createdAt: string) => (
        <div className="text-zinc-800 text-xs font-medium font-['Montserrat']">
          { createdAt ? moment(createdAt).format("DD MMM, YYYY hh:mm A") : "-"}
        </div>
      ),
    },
    {
      title: "Amount (KWD)",
      dataIndex: "amount_kwd",
      key: "amount_kwd",
      sorter: (a, b) => parseFloat(a.amount_kwd) - parseFloat(b.amount_kwd),
      render: (amount: string) => (
        <div className="text-zinc-800 text-xs font-medium font-['Montserrat']">
          {parseFloat(amount).toLocaleString()} KWD
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      filters: [
        { text: "Pending", value: "Pending" },
        { text: "Approved", value: "Approved" },
        { text: "Rejected", value: "Rejected" },
      ],
      filteredValue: currentFilters?.filters?.status || null,
      render: (status: string) => (
        <div className="text-zinc-800 text-xs font-medium font-['Montserrat']">
          {status}
        </div>
      ),
    },
  ];

  const onSearch = (e) => {
    clearTimeout(searchTimer);
    searchTimer = setTimeout(() => {
      setSearchWord(e.target.value);
      onSearchChange(e.target.value);
    }, 1000);
  };

  const handleTableChange = (pagination, filters, sorter) => {
    onFilterChange({ filters, sorter });
  };

  return (
    <div className="w-full flex flex-col justify-start items-start shadow-md bg-white rounded-lg gap-5 py-5">
      <div className="w-full inline-flex justify-start items-center gap-3.5 px-5">
        <div className="w-full flex items-center justify-between rounded-t-lg bg-white">
          <div className="flex-1 justify-start text-zinc-800 text-sm font-semibold font-['Montserrat']">
            Client Transactions
          </div>
          <div className="flex items-center">
            <CustomSearch onChange={(event) => onSearch(event)} />
          </div>
        </div>
      </div>

      <div className="w-full px-5 bg-white [&_.ant-table]:font-['Montserrat'] [&_.ant-table-thead>tr>th]:bg-slate-200 [&_.ant-table-thead>tr>th]:border-b [&_.ant-table-thead>tr>th]:border-gray-300 [&_.ant-table-thead>tr>th]:text-sky-900 [&_.ant-table-thead>tr>th]:text-xs [&_.ant-table-thead>tr>th]:font-semibold [&_.ant-table-thead>tr>th]:font-['Montserrat'] [&_.ant-table-thead>tr>th]:px-5 [&_.ant-table-thead>tr>th]:py-3 [&_.ant-table-tbody>tr>td]:border-b [&_.ant-table-tbody>tr>td]:border-zinc-300 [&_.ant-table-tbody>tr>td]:px-5 [&_.ant-table-tbody>tr>td]:py-4 [&_.ant-table-tbody>tr>td]:bg-white [&_.ant-table-tbody>tr:hover>td]:bg-slate-50 [&_.ant-pagination]:mx-5 [&_.ant-pagination]:my-4 [&_.ant-pagination]:font-['Montserrat'] [&_.ant-table-container]:border-none [&_.ant-table-content]:overflow-x-auto [&_.ant-empty-description]:font-['Montserrat'] [&_.ant-empty-description]:text-zinc-500">
        <CustomTable
          data={data?.data?.items || []}
          loading={loading}
          showPagination={true}
          pageSize={data?.data?.pageSize}
          totalCount={data?.data?.totalCount}
          page={data?.data?.currentPage}
          onPageChange={onPageChange}
          handleTableChange={handleTableChange}
          columns={columns}
          style={{ width: "100%" }}
        />
      </div>
    </div>
  );
};

export default TransactionsTable;
